import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import ActionDropdown from '../../components/ui/ActionDropdown';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import { RiComputerLine, RiEditLine, RiDeleteBinLine, RiEyeLine, RiErrorWarningLine } from 'react-icons/ri';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { DeviceStatus } from '../../types/device';
import deviceService from '../../services/deviceService';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../queryClient';
import { useDeleteDevice } from '../../hooks/mutations/useDeviceMutations';
import { useActiveBrands } from '../../hooks/useBrands';
import { useActiveDeviceTypes } from '../../hooks/useDeviceTypes';
import { PAGINATION_CONFIG } from '../../config/pagination';
import Pagination from '../../components/ui/Pagination';
import { formatDateTimeFull, getLocalDateString, toIctInstant } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import { usePermission } from '../../hooks/usePermission';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { DeviceStatusBadge } from '../../components/ui/StatusBadge';
import { detailNavState } from '../../components/detail';
import { DEVICE_NEW_PATH, deviceDetailUrl, deviceEditUrl } from '../../utils/deviceLinks';

// ใช้ API จริงและ interface จากไฟล์ types/device.ts
import { Device } from '../../types/device';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';
import { deviceDeleteConfirmText } from '../../utils/deviceOptionLabel';

/**
 * หน้ารายการอุปกรณ์ — ดู/เพิ่ม/แก้ไขเป็นหน้าเต็มตั้งแต่ 28 ต.ค. 2026 (มติ PO · เดิมเป็นโมดัลในหน้านี้)
 *   ดู     → /master-data/devices/:id        (DeviceViewPage)
 *   แก้ไข  → /master-data/devices/:id/edit   (DeviceFormPage)
 *   เพิ่ม   → /master-data/devices/new        (DeviceFormPage)
 * ลิงก์เก่า ?view=<id> → App.tsx ครอบหน้านี้ด้วย LegacyDetailRedirect (หน้านี้ไม่ mount เลยเมื่อมีพารามิเตอร์เก่า)
 * ลบเดี่ยว/หลายรายการยังเป็น confirm ในหน้านี้ (action สั้น ไม่ใช่ "ดู/แก้ทั้งเรคคอร์ด")
 *
 * กติกาฟอร์ม (ผู้จำหน่าย/ราคาซื้อบังคับเมื่อไร ฯลฯ) ย้ายไป components/device/deviceForm.ts
 * re-export ไว้ให้ผู้เรียกเดิม
 */
export { isDeviceProcurementRequired } from '../../components/device/deviceForm';

/**
 * ป้ายเตือน "ของยังไม่กลับ" — feedback amintra (Accounting) 2026-09-08
 *
 * *"ถ้าสถานะยังไม่ขึ้นคืนสำเร็จ อุปกรณ์ควรต้องยังไม่พร้อมใช้งาน แต่ตอนนี้อุปกรณ์ดันขึ้น
 * สถานะพร้อมใช้"* — บน prod มี 42 เครื่องที่ใบตามเก็บยังเปิดค้าง กระจายอยู่ 4 สถานะ:
 *   AVAILABLE 26 · DAMAGED 9 · RENTED 6 · LOST 1
 *
 * **จงใจให้เป็นป้าย "เสริม" ไม่ใช่ "แทนที่" สถานะเดิม** (ข้อมูลจาก krp-2025-5c):
 * ถ้าไปแทนที่คำว่า "พร้อมใช้งาน" จะครอบแค่ 26 จาก 42 และ **พลาดกลุ่มที่อันตรายที่สุด
 * คือ 5 เครื่องที่ RENTED อยู่กับสัญญาอื่น** — คนอ่านจะเห็นแค่ "กำลังเช่า" แล้วคิดว่าปกติ
 * ทั้งที่นั่นคือสภาพ "ของชิ้นเดียวอยู่ 2 ที่" ที่เราพยายามทำให้มองเห็นพอดี
 *
 * ของนับจำนวนก็ต้องเตือนได้ (แสดงคู่กับ "—") เพราะใบตามเก็บผูกกับ device_id ไม่ได้
 * แยกตามวิธีติดตามสต็อก
 */
const PendingReturnBadge: React.FC<{ record: Device }> = ({ record }) => {
  if (!record.has_pending_return) return null;
  const n = record.pending_return_count || 0;
  return (
    <div
      className="mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-warning-100 text-warning-800 border border-warning-200"
      title={
        `ยังมีรายการตามเก็บที่ยังไม่ปิด ${n} รายการ — ของอาจยังอยู่กับลูกค้า · ` +
        'ระบบจะไม่ให้นำเครื่องนี้ไปทำสัญญาใหม่จนกว่าจะบันทึกรับคืน หรือยกเลิกรายการตามเก็บ'
      }
    >
      ⚠ รอรับคืน{n > 1 ? ` (${n})` : ''}
    </div>
  );
};

/**
 * ป้าย "🔒 ผูกกับสัญญา …" — fix ค (20 ก.ย. 2026): เครื่องที่ขึ้นว่า "พร้อมใช้งาน" แต่ยังถูกสัญญาที่
 * ยังไม่จบ (DRAFT/ACTIVE/PENDING_CANCELLATION) ผูกอยู่ — backend ส่งเหตุผล + ทางออกมาให้ครบแล้ว
 * (`rental_hold.message`) ที่นี่แค่แสดงผล ห้าม derive เงื่อนไขเอง (ดู utils/rentalHold.ts)
 *
 * คู่ขนานกับ `PendingReturnBadge` แต่คนละสี (info ไม่ใช่ warning) และคนละความหมาย: "รอรับคืน" =
 * ของอาจยังอยู่กับลูกค้าจริง ๆ (ยังไม่ยืนยัน) ส่วน "ผูกกับสัญญา" = ระบบยืนยันแล้วว่าสัญญาไหนยังถือ
 * เครื่องนี้อยู่ (fix ก) และมีทางปลดชัดเจน (ปิดใบแจ้งยกเลิกที่หน้าสัญญานั้น)
 */
const DeviceRentalHoldBadge: React.FC<{ record: Device }> = ({ record }) => {
  const hold = record.rental_hold;
  if (!hold) return null;
  return (
    <Link
      to={`/rentals/view/${hold.rental_id}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-info-100 text-info-800 border border-info-200 hover:bg-info-200"
      title={hold.message}
    >
      🔒 ผูกกับสัญญา {hold.rental_number || `#${hold.rental_id}`}
      {hold.goods_returned ? ' · รับคืนแล้ว' : ''}
    </Link>
  );
};

/** ค่า id จาก query string — ใช้ไม่ได้ = null (ไม่เดา) */
const parsePositiveIntParam = (raw: string | null): number | null => {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
};
const parseBoolParam = (raw: string | null): boolean | null =>
  raw === 'true' ? true : raw === 'false' ? false : null;

const DevicePage: React.FC = () => {
  // Permission hooks
  const { canView, canCreate, canEdit, canDelete, canExport } = usePermission();

  // State สำหรับ pagination และ search
  // KD3 S2482 (REGR-MOVE-008): คำค้นอยู่ใน URL (?q=) → ออกไปหน้าอื่นแล้วกด Back คำค้น+ผลลัพธ์ไม่หาย
  const [searchTerm, setSearchTerm] = useState<string>(() => new URLSearchParams(window.location.search).get('q') || '');
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);

  // FINDING-S980-A FIX (refactored S1035): consume itemsPerPage via shared hook
  useUserPageSize(pageSize, setPageSize);

  // BUG-181 FIX: Two-way URL filter sync using useSearchParams
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStatusParam = searchParams.get('status');
  // OBS-S1104-001/OBS-S1107-001 FIX (S1109 propagation): read `page` param from URL
  const urlPageParam = searchParams.get('page');

  // OBS-S1104-001 FIX (S1109): read `page` from URL on init + defensive parse + fail-open
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });

  // State สำหรับ filters
  // FB-2026-0020: หน้า "สรุปคงเหลือ" (/stock/summary) ลิงก์มาพร้อม ?device_type_id=&brand_id=&device_model_id=&is_serialized=
  // → อ่านค่าเริ่มต้นจาก URL และเขียนกลับใน syncFiltersToUrl (ไม่งั้นลิงก์ค้างใน URL หลังผู้ใช้เปลี่ยนตัวกรอง)
  const [filterDeviceType, setFilterDeviceType] = useState<number | null>(() => parsePositiveIntParam(searchParams.get('device_type_id')));
  const [filterBrand, setFilterBrand] = useState<number | null>(() => parsePositiveIntParam(searchParams.get('brand_id')));
  // ตัวกรอง 2 ตัวนี้ไม่มีช่องบนจอ — มาจากลิงก์เท่านั้น แสดงเป็นแถบ "กรองจากหน้าสรุปคงเหลือ" ให้ล้างได้
  const [filterModelId, setFilterModelId] = useState<number | null>(() => parsePositiveIntParam(searchParams.get('device_model_id')));
  const [filterSerialized, setFilterSerialized] = useState<boolean | null>(() => parseBoolParam(searchParams.get('is_serialized')));
  // "พร้อมใช้" (หยิบได้จริง) / "รอปลดล็อก" จากหน้าสรุปคงเหลือ — ไม่งั้นกด 46 แล้วเห็น 48
  const [filterPickable, setFilterPickable] = useState<boolean | null>(() => parseBoolParam(searchParams.get('pickable')));
  const [filterStatus, setFilterStatus] = useState<string>(() => {
    const statusMap: Record<string, string> = {
      available: 'AVAILABLE',
      rented: 'RENTED',
      maintenance: 'MAINTENANCE',
      damaged: 'DAMAGED',
      lost: 'LOST',
      disposed: 'DISPOSED',
      issued: 'ISSUED',
      reserved: 'RESERVED',
    };
    return statusMap[urlStatusParam?.toLowerCase() || ''] || '';
  });

  // OBS-S1104-001 FIX (S1109): Sync filter + page state back to URL
  // - prev-callback pattern preserves OTHER URL params (e.g., view=)
  // - page=1 ไม่ใส่ใน URL (clean URL) · page>1 ใส่ใน URL
  const syncFiltersToUrl = useCallback((status: string, page: number, search: string, extra: Record<string, string | null>) => {
    const reverseStatusMap: Record<string, string> = {
      AVAILABLE: 'available',
      RENTED: 'rented',
      MAINTENANCE: 'maintenance',
      DAMAGED: 'damaged',
      LOST: 'lost',
      DISPOSED: 'disposed',
      ISSUED: 'issued',
      RESERVED: 'reserved',
    };
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      // Update status param
      if (status && reverseStatusMap[status]) {
        newParams.set('status', reverseStatusMap[status]);
      } else {
        newParams.delete('status');
      }
      if (search) newParams.set('q', search);
      else newParams.delete('q');
      Object.entries(extra).forEach(([k, v]) => (v ? newParams.set(k, v) : newParams.delete(k)));
      // OBS-S1104-001: only add page param when > 1 (clean URL when on first page)
      if (page > 1) {
        newParams.set('page', String(page));
      } else {
        newParams.delete('page');
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterStatus, currentPage, searchTerm, {
      device_type_id: filterDeviceType ? String(filterDeviceType) : null,
      brand_id: filterBrand ? String(filterBrand) : null,
      device_model_id: filterModelId ? String(filterModelId) : null,
      is_serialized: filterSerialized === null ? null : String(filterSerialized),
      pickable: filterPickable === null ? null : String(filterPickable),
    });
  }, [filterStatus, currentPage, searchTerm, filterDeviceType, filterBrand, filterModelId, filterSerialized, filterPickable, syncFiltersToUrl]);

  // OBS-S1107-001 FIX (S1109) + BUG-S771-PAGERESET (S771): reset currentPage to 1 when ANY
  // filter/search content changes.
  // - เดิม watch เฉพาะ filterStatus → เปลี่ยน search / brand / deviceType ตอนอยู่หน้าลึก (เช่นหน้า 3)
  //   ไม่ reset page → paged slice out-of-range → ตารางขึ้น "ไม่มีข้อมูล" ทั้งที่ count badge บอกว่ามีผล
  //   + pagination footer หาย = ผู้ใช้ติดกับ (กลับหน้า 1 ไม่ได้นอกจากล้าง filter). ขยายให้ครอบทุก input
  //   ที่กระทบผลลัพธ์ (search + 3 filter) ผ่าน composite-key ref (mount-safe: initial key ตรง → ไม่ reset ตอน mount).
  const filterKey = `${filterStatus}|${filterBrand}|${filterDeviceType}|${filterModelId}|${filterSerialized}|${searchTerm}`;
  const prevFilterKeyRef = React.useRef<string>(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [filterKey]);

  const navigate = useNavigate();
  const location = useLocation();
  // from = URL รายการพร้อมตัวกรอง — หน้ารายละเอียด/แก้ไขกด "กลับ" แล้วได้ตัวกรองเดิม

  const deleteDeviceMutation = useDeleteDevice();

  // Query hooks สำหรับดึงข้อมูล brands, device types ที่ active เท่านั้น
  // KD3 S2470: lookup ยี่ห้อ/ผู้จำหน่ายยิงเฉพาะเมื่อมีสิทธิ์ — role ที่มีแค่ devices.view (เช่นกดการ์ด "จองไว้" จากแดชบอร์ด)
  // เดิมได้ 403 /brands/active + /suppliers/active ซ้ำ ๆ และ console error 28 บรรทัดทุกครั้งที่เปิดหน้า
  const { data: brandsData } = useActiveBrands({ enabled: canView('brands') });
  const { data: deviceTypesData } = useActiveDeviceTypes();
  
  // Extract data - brands คืนมาเป็น array โดยตรง, deviceTypes คืนมาเป็น object ที่มี items
  const brands = brandsData || [];
  const deviceTypes = deviceTypesData?.items || [];

  // State สำหรับการเลือกและลบหลายรายการ
  const [selectedDevices, setSelectedDevices] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  // Clear selection when filters or pagination change
  useEffect(() => {
    setSelectedDevices([]);
  }, [currentPage, pageSize, searchTerm, filterDeviceType, filterBrand, filterStatus, filterModelId, filterSerialized]);

  // React Query สำหรับดึงข้อมูลอุปกรณ์
  // STOCK-P0 (B3): filter ชุดเดียวใช้ทั้งตารางและปุ่มส่งออก — เดิม export เรียก
  // exportToExcel() โดยไม่ส่ง argument เลย ทั้งที่ service รับ params ได้
  // → ไฟล์ที่ได้เป็นข้อมูลทั้งระบบ ไม่ตรงกับที่เห็นบนจอ (bug class export-filter-parity)
  // สร้างเป็น helper เดียวเพื่อไม่ให้สองทางแยกจากกันอีกเวลาเพิ่ม filter ใหม่
  const buildDeviceFilterParams = useCallback(() => {
    const params: Record<string, unknown> = {};
    if (searchTerm) params.search = searchTerm;
    if (filterDeviceType) params.device_type_id = filterDeviceType;
    if (filterBrand) params.brand_id = filterBrand;
    if (filterStatus) params.status = filterStatus;
    if (filterModelId) params.device_model_id = filterModelId;
    if (filterSerialized !== null) params.is_serialized = filterSerialized;
    if (filterPickable !== null) params.pickable = filterPickable;
    return params;
  }, [searchTerm, filterDeviceType, filterBrand, filterStatus, filterModelId, filterSerialized, filterPickable]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [...queryKeys.devices.list(), { 
      page: currentPage, 
      limit: pageSize,
      search: searchTerm,
      device_type_id: filterDeviceType,
      brand_id: filterBrand,
      status: filterStatus,
      device_model_id: filterModelId,
      is_serialized: filterSerialized,
      pickable: filterPickable,
    }],
    queryFn: async () => {
      return await deviceService.getDevices({
        page: currentPage,
        limit: pageSize,
        ...buildDeviceFilterParams(),
      });
    },
  });
  
  // ข้อมูลอุปกรณ์จาก React Query
  const devices = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, pageSize, setCurrentPage, isLoading);
  
  // รายการที่ต่ำกว่าขั้นต่ำ — ยอดรวม+ตัวอย่างจาก backend (นับทั้งระบบทุกหน้า ไม่ใช่เฉพาะหน้าปัจจุบัน)
  // fallback นับจากหน้าปัจจุบันถ้า backend ยังไม่ส่งค่า (deploy skew)
  const pageLowStockDevices = devices.filter(device =>
    !device.is_serialized && device.quantity <= device.minimum_quantity
  );
  const lowStockDevices = data?.low_stock_items ?? pageLowStockDevices;
  const lowStockTotal = data?.low_stock_total ?? pageLowStockDevices.length;
  // feedback tirawat 21 ก.ย. 2026: "และอื่นๆ อีก N รายการ" กดดูไม่ได้ → พับไว้ 5 รายการ กดขยายดูทั้งหมด
  // (backend ส่งมาทั้งหมดแล้ว เพดาน LOW_STOCK_BANNER_CAP=100 · เกินเพดานบอกตรง ๆ ว่าแสดงกี่จากกี่)
  const [lowStockExpanded, setLowStockExpanded] = useState(false);
  const LOW_STOCK_PREVIEW_COUNT = 5;
  const lowStockPreview = lowStockExpanded
    ? lowStockDevices
    : lowStockDevices.slice(0, LOW_STOCK_PREVIEW_COUNT);
  const lowStockHiddenCount = lowStockTotal - lowStockPreview.length;
  
  // Loading state จาก mutation และ query
  const loading = isLoading || deleteDeviceMutation.isPending;

  // ดู/แก้ไข/เพิ่ม = หน้าเต็ม (พก URL รายการไปใน state.from)
  const handleAddDevice = () => navigate(DEVICE_NEW_PATH, { state: detailNavState(location) });
  const handleViewDevice = (device: Device) => navigate(deviceDetailUrl(device.id), { state: detailNavState(location) });
  const handleEditDevice = (device: Device) => navigate(deviceEditUrl(device.id), { state: detailNavState(location) });

  // ฟังก์ชันสำหรับลบอุปกรณ์
  const handleDeleteDevice = (device: Device) => {
    showConfirm(
      'ยืนยันการลบอุปกรณ์',
      deviceDeleteConfirmText(device),
      async () => {
        try {
          deleteDeviceMutation.mutate(device.id);
        } catch (error) {
          console.error('Error deleting device:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบอุปกรณ์ได้', 'error');
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // ฟังก์ชันสำหรับจัดการการบำรุงรักษา

  
  // ฟังก์ชันสำหรับการเลือกอุปกรณ์ทั้งหมด
  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedDevices(devices.map(device => device.id));
    } else {
      setSelectedDevices([]);
    }
  };
  
  // ฟังก์ชันสำหรับการเลือกอุปกรณ์แต่ละรายการ
  const handleSelectDevice = (deviceId: number, selected: boolean) => {
    if (selected) {
      setSelectedDevices(prev => [...prev, deviceId]);
    } else {
      setSelectedDevices(prev => prev.filter(id => id !== deviceId));
    }
  };
  
  // ฟังก์ชันสำหรับการลบอุปกรณ์หลายรายการ
  const handleBulkDelete = async () => {
    if (selectedDevices.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกอุปกรณ์ที่ต้องการลบ', 'info');
      return;
    }

    showConfirm(
      'ยืนยันการลบอุปกรณ์',
      `คุณต้องการลบอุปกรณ์ ${selectedDevices.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedDevices, id => deviceService.deleteDevice(id));
          setSelectedDevices(result.failed.map(f => f.id));
          refetch();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบอุปกรณ์ ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('อุปกรณ์', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // ฟังก์ชันสำหรับรีเฟรชข้อมูล
  const handleRefresh = () => {
    setSearchTerm('');
    setFilterDeviceType(null);
    setFilterBrand(null);
    setFilterStatus('');
    setFilterModelId(null);
    setFilterSerialized(null);
    setCurrentPage(1);
    refetch();
  };

  // State สำหรับ Export Loading
  const [isExporting, setIsExporting] = useState(false);

  // ฟังก์ชันสำหรับส่งออก Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const blob = await deviceService.exportToExcel(buildDeviceFilterParams());
      downloadExcelFile(blob, `devices_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // สร้าง filters configuration สำหรับ SearchFilterCard
  const filters: FilterConfig[] = [
    {
      key: 'device_type',
      label: 'ประเภทอุปกรณ์',
      type: 'searchable-select',
      options: deviceTypes.map(type => ({ value: type.id, label: type.name })),
      placeholder: 'ประเภททั้งหมด',
      tagColor: 'primary',
    },
    {
      key: 'brand',
      label: 'ยี่ห้อ',
      type: 'searchable-select',
      options: brands.map(brand => ({ value: brand.id, label: brand.name })),
      placeholder: 'ยี่ห้อทั้งหมด',
      tagColor: 'success',
    },
    {
      key: 'status',
      label: 'สถานะ',
      type: 'select',
      options: [
        { value: DeviceStatus.AVAILABLE, label: 'พร้อมใช้งาน' },
        { value: DeviceStatus.RENTED, label: 'กำลังใช้งาน' },
        { value: DeviceStatus.MAINTENANCE, label: 'ซ่อมบำรุง' },
        { value: DeviceStatus.DAMAGED, label: 'ชำรุด' },
        { value: DeviceStatus.LOST, label: 'สูญหาย' },
        { value: DeviceStatus.DISPOSED, label: 'จำหน่ายออก' },
        // Stock Phase 2: ต้องมีในตัวกรองด้วย ไม่งั้นกรองหาเครื่องที่ถูกเบิกไปใช้ภายในไม่ได้
        { value: DeviceStatus.ISSUED, label: 'เบิกไปใช้ภายใน' },
        // ใบงานเตรียมเครื่อง (PO 2026-09-08): เหตุผลเดียวกัน — Support ต้องกรองหาเครื่องที่
        // ถูกกันไว้ให้ใบงานได้ ไม่งั้นเครื่องหายจากทุกตัวกรองโดยไม่มีใครรู้ว่าไปอยู่ไหน
        { value: DeviceStatus.RESERVED, label: 'จองไว้ (ใบงานเตรียมเครื่อง)' },
      ],
      placeholder: 'สถานะทั้งหมด',
      tagColor: 'warning',
    },
  ];

  // Filter values object
  const filterValues = {
    device_type: filterDeviceType || '',
    brand: filterBrand || '',
    status: filterStatus || '',
  };

  // Handle filter change
  const handleFilterChange = (key: string, value: any) => {
    if (key === 'device_type') {
      setFilterDeviceType(value ? Number(value) : null);
    } else if (key === 'brand') {
      setFilterBrand(value ? Number(value) : null);
    } else if (key === 'status') {
      setFilterStatus(value);
      // pickable มีความหมายเฉพาะสถานะพร้อมใช้งาน — ผู้ใช้เปลี่ยนสถานะเองแล้วต้องไม่ค้าง (ไม่งั้นได้ 0 แถวเงียบ ๆ)
      if (value !== 'AVAILABLE') setFilterPickable(null);
    }
    setCurrentPage(1);
  };

  // คอลัมน์ของตาราง
  const columns = [
    {
      key: 'select',
      title: (
        <label className="inline-flex items-center justify-center p-1.5 -m-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedDevices.length === devices.length && devices.length > 0}
            // OBS-R109-001 FIX: show indeterminate state when some (not all) rows are selected
            ref={(input) => {
              if (input) {
                input.indeterminate =
                  selectedDevices.length > 0 && selectedDevices.length < devices.length;
              }
            }}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            aria-label="เลือกทั้งหมด"
          />
        </label>
      ),
      width: '50px',
      render: (value: any, record: Device) => (
        // BUG-A11Y-MD-001: padded label → ≥24px touch target (WCAG 2.5.8 AA)
        <label className="inline-flex items-center justify-center p-1.5 -m-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedDevices.includes(record.id)}
            onChange={(e) => handleSelectDevice(record.id, e.target.checked)}
            className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            aria-label={`เลือก ${record.code}`}
          />
        </label>
      ),
    },
    {
      key: 'code',
      title: 'รหัสอุปกรณ์',
      sortable: true,
      render: (value: string | null, record: Device) => (
        <Link
          to={deviceDetailUrl(record.id)}
          state={detailNavState(location)}
          className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
          data-testid="device-code-link"
        >
          {value || `#${record.id}`}
        </Link>
      ),
    },
    {
      key: 'device_type',
      title: 'ประเภท',
      sortable: true,
      render: (value: any, record: any) => {
        // ถ้ามี device_type object จาก API ให้แสดงชื่อ ถ้าไม่มีให้แสดง value ปกติ
        return record.device_type?.name || value || '-';
      }
    },
    {
      key: 'brand',
      title: 'ยี่ห้อ',
      sortable: true,
      render: (value: any, record: any) => {
        // ถ้ามี brand object จาก API ให้แสดงชื่อ ถ้าไม่มีให้แสดง value ปกติ
        return record.brand?.name || value || '-';
      }
    },
    {
      key: 'model',
      title: 'รุ่น',
      sortable: true,
      // KD3 S2518: ชื่อรุ่นจาก master ก่อน (เหมือนคอลัมน์ยี่ห้อ + หน้ารายละเอียด) — ข้อความ model เดิมอาจเก่า ("A06" vs "A06 (4G)")
      render: (value: any, record: Device) => record.device_model?.name || value || '-',
    },
    {
      key: 'serial_number',
      title: 'S/N / จำนวน',
      sortable: true,
      render: (value: any, record: Device) => {
        if (record.is_serialized) {
          return value || '-';
        } else {
          return (
            <div className="text-sm">
              <div className="font-medium">คงเหลือ: {record.quantity} {record.unit}</div>
              {record.quantity <= record.minimum_quantity && (
                <div className="text-danger-600 text-xs">ต่ำกว่าขั้นต่ำ</div>
              )}
            </div>
          );
        }
      }
    },
    {
      key: 'status',
      title: 'สถานะ',
      sortable: true,
      render: (value: string, record: Device) => {
        // PO 2026-09-08: ของนับจำนวนไม่มีสถานะ — ความจริงอยู่ที่ยอดคงเหลืออย่างเดียว
        // (1 แถว = ของหลายชิ้น การโชว์ badge เดียวให้ทั้งแถวคือการโกหก) · ระบบปฏิเสธการแก้
        // สถานะของแถวเหล่านี้แล้ว (PUT /devices/{id} → 400) ถ้ายังโชว์ badge ผู้ใช้จะพยายาม
        // กดแก้แล้วเจอ error โดยไม่จำเป็น → โชว์ยอดคงเหลือแทน ตรงกับสิ่งที่แก้ได้จริง
        if (record.is_serialized === false) {
          // ไม่โชว์ยอดซ้ำที่นี่ — คอลัมน์ "S/N / จำนวน" ข้าง ๆ แสดงยอดคงเหลือ + ป้ายต่ำกว่าขั้นต่ำ
          // อยู่แล้ว · ช่องนี้บอกแค่ว่า "ของประเภทนี้ไม่มีสถานะ" เพื่อไม่ให้ดูเหมือนข้อมูลหาย
          return (
            <div>
              <span
                className="text-sm text-secondary-400"
                title="อุปกรณ์แบบนับจำนวนไม่มีสถานะ — ความถูกต้องอยู่ที่ยอดคงเหลืออย่างเดียว"
              >
                —
              </span>
              <PendingReturnBadge record={record} />
              <DeviceRentalHoldBadge record={record} />
            </div>
          );
        }

        // Normalize status value to uppercase for the centralized StatusBadge
        const normalizedStatus = value?.toUpperCase() as DeviceStatus;
        // Phase 2 คลังอุปกรณ์: เครื่องที่ถูกเบิกไปใช้ภายในต้องบอกได้ว่า "อยู่กับใคร"
        // ไม่งั้นป้าย ISSUED ตอบได้แค่ว่าของไม่อยู่ในคลัง แต่ไม่รู้ว่าตามเก็บจากใคร
        const isIssued = normalizedStatus === DeviceStatus.ISSUED;
        const holder = isIssued ? record.issued_to_display : null;
        const issueNumber = isIssued ? record.current_stock_issue_number : null;
        return (
          <div>
            <DeviceStatusBadge status={normalizedStatus} size="sm" bordered />
            <PendingReturnBadge record={record} />
            <DeviceRentalHoldBadge record={record} />
            {holder && (
              <div
                className="text-xs text-secondary-500 mt-0.5 max-w-[10rem] truncate"
                title={issueNumber ? `${holder} · ใบเบิก ${issueNumber}` : holder}
              >
                ผู้ยืม: {holder}
                {issueNumber && <span className="block">{issueNumber}</span>}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'created_at',
      title: 'วันที่สร้าง',
      sortable: true,
      render: (value: string | null) => {
        if (!value) return '-';
        const d = new Date(toIctInstant(value));
        return (
          <span title={formatDateTimeFull(value)} className="whitespace-nowrap">
            {d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'Asia/Bangkok' })}
          </span>
        );
      }
    },
    {
      key: 'updated_at',
      title: 'อัพเดทล่าสุด',
      sortable: true,
      render: (value: string | null) => {
        if (!value) return '-';
        const d = new Date(toIctInstant(value));
        return (
          <span title={formatDateTimeFull(value)} className="whitespace-nowrap">
            {d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'Asia/Bangkok' })}
          </span>
        );
      }
    },
    {
      key: 'actions',
      title: 'จัดการ',
      sticky: true,
      render: (_: any, row: Device) => (
        <ActionDropdown
          actions={[
            {
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => handleViewDevice(row)
            },
            canEdit('devices') ? {
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => handleEditDevice(row)
            } : null,
            (canEdit('devices') || canDelete('devices')) ? {
              divider: true
            } : null,
            canDelete('devices') ? {
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDeleteDevice(row),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            } : null
          ].filter(Boolean)}
          buttonIcon={null}
          buttonLabel=""
        />
      )
    }
  ];

  return (
    <Layout>
      <div className="w-full">
        {/* Page Header - Clean & Consistent */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiComputerLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">อุปกรณ์</h1>
              <p className="text-xs text-secondary-500 mt-0.5">จัดการข้อมูลอุปกรณ์ทั้งหมดในระบบ</p>
            </div>
          </div>
          {totalItems > 0 && (
            <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
              {totalItems.toLocaleString()} รายการ
            </span>
          )}
        </div>
        
        {/* Stock Summary Section */}
        {lowStockTotal > 0 && (
          <div className="mb-5 flex items-start gap-3 bg-warning-50 border border-warning-200 rounded-xl p-4">
            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-warning-100 flex items-center justify-center">
              <RiErrorWarningLine className="w-5 h-5 text-warning-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-warning-800">
                {/* E4: เกณฑ์จริงคือ quantity <= minimum_quantity (รวมเท่ากับ) — ข้อความเดิม
                    "ที่ต่ำกว่า" ทำให้ผู้ใช้นับเองแล้วไม่ตรงกับตัวเลขในแบนเนอร์ */}
                มีอุปกรณ์ {lowStockTotal} รายการ ที่ถึงหรือต่ำกว่าจำนวนขั้นต่ำ
              </h3>
              <ul className="mt-1.5 text-sm text-warning-700 space-y-0.5 list-disc list-inside">
                {lowStockPreview.map(device => (
                  <li key={device.id} className="truncate">
                    <span className="font-medium">{device.code}</span>
                    {' '}{device.device_type?.name || ''} {device.brand?.name || ''} {device.device_model?.name || device.model || ''}
                    <span className="ml-1.5 text-xs text-warning-700">
                      (เหลือ {device.quantity}/{device.minimum_quantity} {device.unit})
                    </span>
                  </li>
                ))}
              </ul>
              {!lowStockExpanded && lowStockDevices.length > LOW_STOCK_PREVIEW_COUNT && (
                <button
                  type="button"
                  onClick={() => setLowStockExpanded(true)}
                  aria-expanded={false}
                  className="mt-1.5 text-sm font-semibold text-warning-800 hover:text-warning-900 underline underline-offset-2"
                >
                  ดูทั้งหมด (อีก {lowStockHiddenCount} รายการ)
                </button>
              )}
              {lowStockExpanded && (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  {lowStockHiddenCount > 0 && (
                    <span className="text-warning-700">
                      แสดง {lowStockPreview.length} จาก {lowStockTotal} รายการ
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setLowStockExpanded(false)}
                    aria-expanded={true}
                    className="font-semibold text-warning-800 hover:text-warning-900 underline underline-offset-2"
                  >
                    ย่อรายการ
                  </button>
                </div>
              )}
              {!lowStockExpanded && lowStockDevices.length <= LOW_STOCK_PREVIEW_COUNT && lowStockHiddenCount > 0 && (
                // backend รุ่นเก่า (ส่งมาแค่ 5) — ไม่มีรายการให้ขยาย จึงบอกเป็นข้อความเหมือนเดิม
                <p className="mt-1.5 text-sm text-warning-500">และอื่นๆ อีก {lowStockHiddenCount} รายการ</p>
              )}
            </div>
          </div>
        )}
        
        {/* แสดง Error Message ถ้ามีข้อผิดพลาด */}
        {error && (
          <div className="mb-4 flex items-start gap-3 p-4 border border-danger-200 rounded-xl bg-danger-50">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-danger-100 flex items-center justify-center">
              <RiErrorWarningLine className="w-4 h-4 text-danger-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-danger-800">ไม่สามารถโหลดข้อมูลอุปกรณ์ได้</p>
              <button
                type="button"
                className="mt-1.5 text-xs font-medium text-danger-600 hover:text-danger-700 underline focus:outline-none"
                onClick={() => refetch()}
              >
                ลองใหม่อีกครั้ง
              </button>
            </div>
          </div>
        )}
        

        {/* Action Buttons Row */}
        <ActionButtonsRow
          onAdd={canCreate('devices') ? handleAddDevice : undefined}
          addLabel="เพิ่มอุปกรณ์"
          onRefresh={handleRefresh}
          refreshLoading={isFetching}
          onExport={canExport('devices') ? handleExportExcel : undefined}
          exportDisabled={devices.length === 0}
        />

        {/* Search and Filter Card */}
        <SearchFilterCard
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="ค้นหาอุปกรณ์ (รหัส, S/N, IMEI, รุ่น)..."
          filters={filters}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          onClearAll={handleRefresh}
        />

        {/* FB-2026-0020: ตัวกรองจากลิงก์หน้า "สรุปคงเหลือ" ที่ไม่มีช่องบนจอ — ต้องเห็นและล้างได้ ไม่งั้นรายการ "หาย" เงียบ ๆ */}
        {(filterModelId !== null || filterSerialized !== null || filterPickable !== null) && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 text-sm text-primary-800" data-testid="device-link-filter">
            <span>กรองจากหน้าสรุปคงเหลือ:</span>
            {filterModelId !== null && <span className="font-medium">เฉพาะรุ่นที่เลือก</span>}
            {filterSerialized !== null && (
              <span className="font-medium">{filterSerialized ? 'เฉพาะของมี S/N' : 'เฉพาะของนับจำนวน'}</span>
            )}
            {filterPickable !== null && (
              <span className="font-medium">{filterPickable ? 'เฉพาะที่หยิบได้จริง' : 'เฉพาะที่ติดล็อก (รอปลดล็อก)'}</span>
            )}
            <button
              type="button"
              className="ml-auto text-primary-700 underline hover:text-primary-900"
              onClick={() => {
                setFilterModelId(null);
                setFilterSerialized(null);
                setFilterPickable(null);
              }}
            >
              ล้างตัวกรองนี้
            </button>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {canDelete('devices') && (
          <BulkActionsBar
            selectedCount={selectedDevices.length}
            onBulkDelete={handleBulkDelete}
            onClearSelection={() => setSelectedDevices([])}
            loading={bulkLoading}
          />
        )}

        <Table
          columns={canDelete('devices') ? columns : columns.filter(c => c.key !== 'select')}  /* KD3 S2122 (BUG-S2122-004): ไม่มีสิทธิ์ลบ = ไม่มีช่องติ๊ก (ติ๊กแล้วไม่มีอะไรให้ทำ) */
          data={devices}
          emptyText={error ? 'โหลดข้อมูลไม่สำเร็จ' : undefined}
          emptyHint={error ? null : undefined}  /* BUG-S2273-001: โหลดล้ม ≠ ตัวกรองไม่เจอ */
          loading={loading}
          pagination={false}
          searchable={false}
          sortable={true}
          ariaLabel="รายการอุปกรณ์"
        />
        
        {/* Pagination */}
        {!loading && devices.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }}
            showPageSizeSelector={true}
            showSummary={true}
            className="mt-5"
          />
        )}
        

        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default DevicePage;